// ============================================================================
// Google Calendar — Webhook Adapter.
//
// Google Calendar push notifications are HTTP POST requests with:
//   - Headers:
//     - x-goog-channel-id           — the channel id we sent on /watch
//     - x-goog-channel-token        — the shared secret we set on /watch
//     - x-goog-resource-id          — provider-assigned resource id
//     - x-goog-resource-state       — "sync" / "exists" / "not_exists"
//     - x-goog-message-number       — monotonic counter per channel, starts at 1
//     - x-goog-channel-expiration   — RFC 1123 absolute expiration
//     - x-goog-resource-uri         — Google's URL for the watched resource
//   - Body: EMPTY (no event payload) — we only learn "something changed".
//
// Verification:
//   Google does NOT use HMAC here (unlike FB/Line). Instead we rely on the
//   `channel token` we set when subscribing being echoed back verbatim in
//   `x-goog-channel-token`. Timing-safe compare it against the per-instance
//   secret stored on `connector_instances.config` (set by the subscribe flow).
//
// Dedupe:
//   `x-goog-message-number` is monotonic per channel, so a stable event id
//   is `${channelId}:${messageNumber}`. Google retries with the same pair on
//   transport errors so dedupe is reliable.
//
// Downstream:
//   The webhook only TRIGGERS a resolution job. Downstream consumers fetch
//   the delta via `listGoogleEvents({ syncToken })` to build the actual
//   capability events (`calendar.event.*`). The parsed webhook event below
//   carries the channel id + resource state so the resolution job can
//   locate the right instance + syncToken.
//
// Canonical docs:
//   - https://developers.google.com/calendar/api/guides/push#receiving
// ============================================================================

import {
  base64ToBytes,
  timingSafeEqualBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'

// ============================================================================
// Verification
// ============================================================================

/**
 * Public-facing verifier. Accepts the adapter context's headers + the
 * expected channel token, returns true iff `x-goog-channel-token` matches
 * in constant time.
 *
 * Returns `false` on any malformed / missing input rather than throwing so
 * the ingress pipeline can treat all verification failures uniformly.
 */
export function verifyGoogleCalendarSignature(
  headers: Headers,
  expectedChannelToken: string,
): boolean {
  if (!expectedChannelToken || typeof expectedChannelToken !== 'string') return false
  const raw = headers.get('x-goog-channel-token')
  if (!raw) return false

  // `TextEncoder` is always utf-8 — since both strings came through utf-8
  // byte channels (headers + config column) a utf-8 round-trip preserves
  // the bytes exactly.
  const encoder = new TextEncoder()
  const candidate = encoder.encode(raw)
  const expected = encoder.encode(expectedChannelToken)
  return timingSafeEqualBytes(candidate, expected)
}

async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  const expected = pickWatchConfig(ctx.instance.config)
  // Missing expected token → fail closed. Same pattern as the FB/Line
  // adapters — never accept a webhook when the secret config is missing.
  if (!expected?.channelToken) return false
  if (!verifyGoogleCalendarSignature(ctx.headers, expected.channelToken)) return false

  const providedChannelId = ctx.headers.get('x-goog-channel-id')
  if (expected.channelId && providedChannelId !== expected.channelId) return false

  const providedResourceId = ctx.headers.get('x-goog-resource-id')
  if (expected.resourceId && providedResourceId !== expected.resourceId) return false

  return true
}

/**
 * Pull the per-instance channel token out of the `connector_instances.config`
 * JSON column. The subscribe flow writes it there under the `channelToken`
 * key (or `watchChannelToken` for disambiguation when a single instance
 * watches multiple calendars). Accepts both keys for forward-compatibility.
 */
function pickWatchConfig(config: string | null | undefined): {
  channelToken: string | null
  channelId?: string
  resourceId?: string
} | null {
  if (typeof config !== 'string' || config.length === 0) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(config)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const obj = parsed as Record<string, unknown>
  const channelId = typeof obj.channelId === 'string' && obj.channelId.length > 0
    ? obj.channelId
    : undefined
  const resourceId = typeof obj.resourceId === 'string' && obj.resourceId.length > 0
    ? obj.resourceId
    : undefined
  if (typeof obj.watchChannelToken === 'string' && obj.watchChannelToken.length > 0) {
    return { channelToken: obj.watchChannelToken, channelId, resourceId }
  }
  if (typeof obj.channelToken === 'string' && obj.channelToken.length > 0) {
    return { channelToken: obj.channelToken, channelId, resourceId }
  }
  return { channelToken: null, channelId, resourceId }
}

// ============================================================================
// Parsing
// ============================================================================

export type GoogleResourceState = 'sync' | 'exists' | 'not_exists'

export interface GoogleCalendarNotification {
  channelId: string
  resourceId: string
  resourceState: GoogleResourceState
  messageNumber: number
  channelExpiration?: string
  resourceUri?: string
  receivedAt: string
}

async function parseWebhook(ctx: WebhookAdapterContext): Promise<ParsedWebhookEvent[]> {
  return parseGoogleCalendarWebhook(ctx.headers)
}

/**
 * Parse a Google Calendar push notification from its headers. The body is
 * always empty — Google carries all relevant metadata in `x-goog-*` headers.
 *
 * Returns an array so this matches the `WebhookAdapter.parseWebhook`
 * contract, but Google delivers exactly one logical notification per
 * request. The `sync` resource state is Google's "channel handshake" ping
 * that arrives once right after /watch succeeds — we surface it but
 * downstream consumers usually skip emitting for it.
 */
export function parseGoogleCalendarWebhook(
  headers: Headers,
): ParsedWebhookEvent[] {
  const channelId = headers.get('x-goog-channel-id') ?? ''
  const resourceId = headers.get('x-goog-resource-id') ?? ''
  const resourceStateRaw = headers.get('x-goog-resource-state') ?? ''
  const messageNumberRaw = headers.get('x-goog-message-number') ?? ''
  const channelExpiration = headers.get('x-goog-channel-expiration') ?? undefined
  const resourceUri = headers.get('x-goog-resource-uri') ?? undefined

  // Minimum set of headers required for a usable notification. Anything less
  // is a malformed delivery (or a probe) — we return an empty event list so
  // the ingress pipeline responds 200 without trying to emit anything.
  if (!channelId || !resourceId || !resourceStateRaw || !messageNumberRaw) {
    return []
  }

  const resourceState = normaliseResourceState(resourceStateRaw)
  if (!resourceState) return []

  const messageNumber = Number.parseInt(messageNumberRaw, 10)
  if (!Number.isFinite(messageNumber) || messageNumber < 1) return []

  const notification: GoogleCalendarNotification = {
    channelId,
    resourceId,
    resourceState,
    messageNumber,
    channelExpiration,
    resourceUri,
    receivedAt: new Date().toISOString(),
  }

  // Retry-stable id: `${channelId}:${messageNumber}`. Google resends the same
  // message number on retry, so dedupe across the ingress + the resolution
  // job is safe.
  const eventId = `${channelId}:${messageNumber}`

  // We deliberately do NOT set `capability` here — the actual capability
  // events (`calendar.event.created/updated/deleted`) are emitted by the
  // downstream sync-resolution job after it calls `listGoogleEvents` with
  // the stored syncToken. The ingress pipeline records the notification in
  // the audit log but does not fan it out to subscribers.
  return [{
    eventId,
    eventType: 'calendar.change.notification',
    payload: notification,
  }]
}

function normaliseResourceState(
  value: string,
): GoogleResourceState | null {
  const lower = value.toLowerCase()
  if (lower === 'sync' || lower === 'exists' || lower === 'not_exists') {
    return lower
  }
  return null
}

// ============================================================================
// Adapter export
// ============================================================================

export const googleCalendarWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}

// ============================================================================
// Exports used by tests only — helps parity with the FB/Line suites.
// ============================================================================

export { base64ToBytes }
